import { useEffect } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { School } from 'lucide-react';

import { ConsoleIdentity } from '@/components/layout/ConsoleIdentity';
import { ConsoleLayout } from '@/components/layout/ConsoleLayout';
import { SecureImage } from '@/components/ui/SecureImage';
import { env } from '@/config/env';
import { ADMIN_NAV } from '@/constants/navigation';
import { ROLE_LABEL } from '@/constants/roles';
import { useAuthStore } from '@/features/auth/store/auth.store';
import { NotificationBell } from '@/features/notifications/components/NotificationBell';
import { SendMessageButton } from '@/features/messaging/components/SendMessageModal';
import { instituteApi } from '@/features/institute/api/institute.api';
import { useInstituteBranding } from '@/features/institute/store/branding.store';

/**
 * The console an institute administers itself from.
 *
 * The same frame as the platform and teacher consoles — a member of staff moving
 * between them should not have to re-learn where anything is. The student panel
 * is the one area drawn differently on purpose, so a child's screen never looks
 * like a staff screen.
 */
export function InstituteLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);
  const branding = useInstituteBranding((state) => state.branding);
  const logoVersion = useInstituteBranding((state) => state.version);
  const loadBranding = useInstituteBranding((state) => state.load);
  const canSendMessages = Boolean(user?.permissions?.includes('MESSAGE_SEND'));

  useEffect(() => {
    void loadBranding();
  }, [loadBranding]);

  /**
   * An account whose password was set for them — by our support team or by an
   * administrator — cannot go anywhere else until they have replaced it. That
   * is what makes handing over a password safe: it stops working the moment it
   * is used.
   */
  if (user?.mustChangePassword && location.pathname !== '/admin/password') {
    return <Navigate to="/admin/password" replace />;
  }

  const signedInAs = user?.instituteRole ?? (user ? ROLE_LABEL[user.role] : '');

  return (
    <ConsoleLayout
      nav={ADMIN_NAV}
      brand={{
        mark: branding?.hasLogo ? (
          <SecureImage
            path={instituteApi.logoPath()}
            alt=""
            reloadKey={logoVersion}
            className="h-full w-full bg-white object-contain p-0.5"
            fallback={<School className="h-[17px] w-[17px]" strokeWidth={1.9} />}
          />
        ) : (
          <School className="h-[17px] w-[17px]" strokeWidth={1.9} />
        ),
        // The institute signed in, never a name baked into the build (§4).
        title: branding?.displayName ?? env.appName,
        // The city when the institute has one on record; otherwise nothing,
        // rather than a stand-in line that names no place.
        subtitle: branding?.city ?? '',
      }}
      // No `account`: this console names the person in the top bar
      // (`ConsoleIdentity`), so repeating it at the foot of the sidebar would
      // print the same two lines twice on every screen.
      onSignOut={async () => {
        await logout();
        navigate('/login', { replace: true });
      }}
      greeting={
        <ConsoleIdentity
          name={user?.fullName ?? 'System Admin'}
          role={signedInAs}
          to="/admin/profile"
        />
      }
      actions={
        <>
          {/* Only for the roles that hold the key. Sending reaches people
              outside the product, so it is not something every signed-in
              member of staff should find in the bar. */}
          {canSendMessages && <SendMessageButton />}
          <NotificationBell />
        </>
      }
    />
  );
}
